import { useCallback, useEffect, useState } from "react";
import { fetchApprovals } from "@/lib/api";
import type { Approval, ApprovalType, ApprovalStatus } from "@/types";

interface UseApprovalsResult {
  pending: Approval[];
  completed: Approval[];
  loading: boolean;
  error: string | null;
  source: "api" | "mock";
  refresh: () => void;
}

function slaSeverityFor(slaMinutes: number | null): "ok" | "warn" | "danger" {
  if (slaMinutes === null) return "ok";
  if (slaMinutes < 30) return "danger";
  if (slaMinutes < 120) return "warn";
  return "ok";
}

function mapApiApproval(raw: Record<string, unknown>): Approval {
  const id = String(raw.id ?? raw.approvalId ?? "");
  const typeRaw = String(raw.type ?? raw.approvalType ?? "transfer").toLowerCase();
  const type: ApprovalType = typeRaw === "wallet" ? "wallet" : typeRaw === "policy" ? "policy" : "transfer";
  const title = String(raw.title ?? raw.description ?? `${type} approval`);
  const meta = String(raw.meta ?? raw.summary ?? "");
  const hint = String(raw.hint ?? "");
  const statusRaw = String(raw.status ?? "pending").toLowerCase();
  const status: ApprovalStatus =
    statusRaw.includes("approved") ? "approved"
    : statusRaw.includes("reject") ? "rejected"
    : statusRaw.includes("escalat") ? "escalated"
    : "pending";
  const approvalsRequired = typeof raw.approvalsRequired === "number" ? raw.approvalsRequired : 1;
  const approvalsDone = typeof raw.approvalsDone === "number" ? raw.approvalsDone : 0;
  const progressPercent = approvalsRequired > 0 ? Math.round((approvalsDone / approvalsRequired) * 100) : null;
  const slaMinutes = typeof raw.slaMinutesRemaining === "number" ? raw.slaMinutesRemaining : null;
  const slaLeft = slaMinutes !== null
    ? slaMinutes >= 60 ? `${Math.floor(slaMinutes / 60)}h ${slaMinutes % 60}m left` : `${slaMinutes}m left`
    : String(raw.slaLeft ?? "");
  const urgent = raw.urgent === true || (slaMinutes !== null && slaMinutes < 60);

  return {
    id, type, title, meta, hint,
    slaLeft, slaSeverity: slaSeverityFor(slaMinutes),
    approvalsRequired, approvalsDone, progressPercent,
    status, urgent,
  };
}

export function useApprovals(): UseApprovalsResult {
  const [pending, setPending] = useState<Approval[]>([]);
  const [completed, setCompleted] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApprovals();
      if (result.reasonCode) {
        setPending([]);
        setCompleted([]);
        setSource("api");
        setError(result.reasonCode);
      } else if (result.approvals.length === 0) {
        setPending([]);
        setCompleted([]);
        setSource("api");
      } else {
        const all = result.approvals.map(mapApiApproval);
        setPending(all.filter(a => a.status === "pending"));
        setCompleted(all.filter(a => a.status !== "pending"));
        setSource("api");
      }
    } catch (error) {
      setPending([]);
      setCompleted([]);
      setSource("api");
      setError(error instanceof Error ? error.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { pending, completed, loading, error, source, refresh: load };
}
